import {
  Group,
  Box,
  ScrollArea,
  Badge,
  Text,
  useMantineTheme,
  ActionIcon,
  Paper,
  Pagination,
  Button,
  TextInput,
  Dialog,
  Select,
} from "@mantine/core";
import {
  Pencil,
  Trash,
  Key,
  FileDots,
  UserPlus,
  Search,
} from "tabler-icons-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  INITIAL_PAGINATION_RESPONSE,
  useUsersQuery,
  useDeleteUserMutation,
  useDeptOptionsQuery,
  useRolesQuery,
} from "services/api";

import Th from "features/backstage/_shared/Th";
import useTotalPages from "features/backstage/_shared/useTotalPages";
import Table from "features/backstage/_shared/Table";

const List = () => {
  const theme = useMantineTheme();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(30);

  const [sort, setSort] = useState<string>("isFromAd");
  const [isDescending, setIsDescending] = useState(false);

  const [search, setSearch] = useState<string>();
  const [searchText, setSearchText] = useState<string>("");

  const [deptName, setDeptName] = useState<string>();
  const [isFromAd, setIsFromAd] = useState<string>();
  const [role, setRole] = useState<string>();
  const [stopYn, setStopYn] = useState<string>();

  const { data: userListData = INITIAL_PAGINATION_RESPONSE } = useUsersQuery({
    page,
    search,
    sort: isDescending ? `-${sort}` : sort,
    deptName,
    isFromAd,
    role,
    stopYn,
    perPage,
  });

  const totalPages = useTotalPages(userListData.totalCount, perPage);

  const [deleteUser] = useDeleteUserMutation();
  const [deleteUserId, setDeleteUserId] = useState<string>();

  const { data: deptOptions = [] } = useDeptOptionsQuery();

  const { roleOptions = [] } = useRolesQuery(undefined, {
    selectFromResult: (result) => ({
      roleOptions: result.data?.map((x) => ({
        label: x.roleName,
        value: x.roleNo,
      })),
    }),
  });

  const isFromAdOptions = [
    { label: "市府人員", value: "true" },
    { label: "系統人員", value: "false" },
  ];

  const stopYnOptions = [
    { label: "停用", value: "true" },
    { label: "啟用", value: "false" },
  ];

  const handleSort = (sortBy: string) => () => {
    setPage(1);

    if (sortBy === sort) {
      setIsDescending((prev) => !prev);
      return;
    }

    setSort(sortBy);
    setIsDescending(false);
  };

  const rows = userListData.items.map((x) => (
    <tr key={x.empNo}>
      <td>
        <Group spacing="sm">
          <Text size="sm" weight={500}>
            {x.empNo}
          </Text>
        </Group>
      </td>
      <td>
        <Group spacing="sm">
          <Text size="sm" weight={500}>
            {x.empName}
          </Text>
        </Group>
      </td>
      <td>{x.deptName}</td>
      <td>
        {!x.isFromAd ? (
          <Badge
            color="cyan"
            variant={theme.colorScheme === "dark" ? "light" : "outline"}
          >
            系統人員
          </Badge>
        ) : (
          <Badge
            color="gray"
            variant={theme.colorScheme === "dark" ? "light" : "outline"}
          >
            市府人員
          </Badge>
        )}
      </td>
      <td style={{ wordBreak: "break-all" }}>{x.email}</td>
      <td>
        {x.roleNos.map((x) => (
          <Text key={x.roleNo}>{x.roleName}</Text>
        ))}
      </td>
      <td>{x.stopYn ? "停用" : "啟用"}</td>
      <td>{x.lastLogon ? new Date(x.lastLogon).toLocaleString() : "-"}</td>
      <td>
        <Group spacing={0} position="right">
          {x.isFromAd ? (
            <>
              <ActionIcon color="gray" component={Link} to={x.empNo}>
                <FileDots size={20} />
              </ActionIcon>
              <ActionIcon
                color="gray"
                component={Link}
                to={`${x.empNo}/reset-password`}
              >
                <Key size={20} />
              </ActionIcon>
            </>
          ) : (
            <>
              <ActionIcon color="gray" component={Link} to={x.empNo}>
                <FileDots size={20} />
              </ActionIcon>
              <ActionIcon color="gray" component={Link} to={`${x.empNo}/edit`}>
                <Pencil size={20} />
              </ActionIcon>
              <ActionIcon
                color="gray"
                component={Link}
                to={`${x.empNo}/reset-password`}
              >
                <Key size={20} />
              </ActionIcon>
              <ActionIcon color="red" onClick={() => setDeleteUserId(x.empNo)}>
                <Trash size={20} />
              </ActionIcon>
            </>
          )}
        </Group>
      </td>
    </tr>
  ));

  return (
    <Box py={8} px={36}>
      <Group position="apart">
        <h2>人員帳號管理</h2>
        <Button
          component={Link}
          to="new"
          leftIcon={<UserPlus />}
          variant="outline"
          color="cyan"
        >
          新增人員帳號
        </Button>
      </Group>
      <Paper
        px={16}
        pt={20}
        pb={8}
        mt={16}
        radius={"lg"}
        shadow={theme.shadows.lg}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setSearch(searchText);
          }}
        >
          <Group mb={16}>
            <TextInput
              placeholder="輸入姓名、編號、Email 或部門關鍵字進行搜尋"
              icon={<Search size={14} />}
              rightSectionWidth={132}
              style={{
                flex: "50% 0 0",
              }}
              value={searchText}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSearchText(event.currentTarget.value)
              }
            />
            <Button color="cyan" type="submit">
              搜尋
            </Button>
          </Group>

          <Group mb={16}>
            <Select
              placeholder="部門"
              clearable
              data={deptOptions}
              value={deptName}
              onChange={setDeptName}
            />

            <Select
              placeholder="人員屬性"
              clearable
              data={isFromAdOptions}
              value={isFromAd}
              onChange={setIsFromAd}
            />

            <Select
              placeholder="帳號權限"
              clearable
              data={roleOptions}
              value={role}
              onChange={setRole}
            />

            <Select
              placeholder="帳號狀態"
              clearable
              data={stopYnOptions}
              value={stopYn}
              onChange={setStopYn}
            />
          </Group>
        </form>
        <ScrollArea>
          <Table>
            <thead>
              <tr>
                <Th
                  sorted={sort === "empNo"}
                  reversed={isDescending}
                  onSort={handleSort("empNo")}
                >
                  編號
                </Th>
                <Th
                  sorted={sort === "empName"}
                  reversed={isDescending}
                  onSort={handleSort("empName")}
                >
                  姓名
                </Th>
                <Th
                  sorted={sort === "deptName"}
                  reversed={isDescending}
                  onSort={handleSort("deptName")}
                >
                  部門
                </Th>
                <Th
                  sorted={sort === "isFromAd"}
                  reversed={isDescending}
                  onSort={handleSort("isFromAd")}
                >
                  人員屬性
                </Th>
                <Th
                  sorted={sort === "email"}
                  reversed={isDescending}
                  onSort={handleSort("email")}
                >
                  Email
                </Th>
                <th>帳號權限</th>
                <Th
                  sorted={sort === "stopYn"}
                  reversed={isDescending}
                  onSort={handleSort("stopYn")}
                >
                  帳號狀態
                </Th>
                <Th
                  sorted={sort === "lastLogon"}
                  reversed={isDescending}
                  onSort={handleSort("lastLogon")}
                >
                  登入時間
                </Th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows
              ) : (
                <tr>
                  <td colSpan={9}>
                    <Text weight={500} align="center">
                      沒有找到相關資料
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>

        <Group mb={16} position="apart">
          <Pagination
            pt={24}
            pb={16}
            total={totalPages}
            page={page}
            color="cyan"
            onChange={setPage}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text mr={16}>共 {userListData.totalCount} 筆資料</Text>

            <Select
              data={[
                { label: "每頁 30 筆", value: "30" },
                { label: "每頁 50 筆", value: "50" },
                { label: "每頁 100 筆", value: "100" },
              ]}
              value={`${perPage}`}
              onChange={(value) => setPerPage(+value)}
            />
          </Box>
        </Group>
      </Paper>

      <Dialog
        opened={deleteUserId !== undefined}
        withCloseButton
        onClose={() => setDeleteUserId(undefined)}
        size="lg"
        radius="md"
      >
        <Group align="center">
          <Text size="sm" style={{ marginBottom: 10 }} weight={500}>
            確定刪除人員資料？
          </Text>
          <Button
            color="red"
            onClick={async () => {
              await deleteUser(deleteUserId).unwrap();
              setDeleteUserId(undefined);
            }}
          >
            確認刪除
          </Button>
        </Group>
      </Dialog>
    </Box>
  );
};

export default List;
